/**
 * Layout do painel — monta a casca de navegação (`components/shell/`) com o REGISTRO de telas do
 * Sentinela (`lib/menu.ts`). Este arquivo só liga o produto à casca: login/logout (Cloudflare Access),
 * papel e usuário da sessão, roteador. Toda a regra de navegação (itens por papel, item ativo, gaveta,
 * trilha, busca, tema, "Sem acesso") mora em `shell/` e no registro.
 *
 * Histórico: até 11/10/2026 o menu era escrito aqui, com "Sair" e tema no fim da rolagem da barra lateral
 * (115 px abaixo da dobra em 1366×768), um filtro de papel à mão só para o Financeiro e uma gaveta sem
 * foco contido. Ver `lib/menu.ts` e `components/shell/README.md`.
 */
import { Suspense, useCallback, useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Carregando } from "./ui/index.js";
import { NovidadesModal } from "./NovidadesModal.js";
import { CascaAdmin } from "./shell/CascaAdmin.js";
import type { PropsLinkShell, UsuarioShell } from "./shell/tipos.js";
import { useTema } from "../lib/tema.js";
import { GRUPOS, ITENS, rotuloDoPapel } from "../lib/menu.js";
import { aplicarContagens, useContagensDoMenu } from "../lib/contagens-menu.js";
import { definirTokenAdmin, definirUsuarioAdmin, lerUsuarioAdmin } from "../lib/api.js";

/**
 * URL de logout do Cloudflare Access — invalida o cookie de sessão
 * do CF Access (caso contrário, mesmo limpando sessionStorage o usuário
 * volta a estar logado no próximo refresh).
 *
 * Team domain visto na auditoria: nocuse.cloudflareaccess.com
 */
const CF_ACCESS_LOGOUT_URL = "https://nocuse.cloudflareaccess.com/cdn-cgi/access/logout";

function fazerLogout(): void {
  // 1. Limpa JWT interno + dados do usuário do sessionStorage
  definirTokenAdmin(null);
  definirUsuarioAdmin(null);
  // 2. Redireciona pra logout do CF Access — após logout, CF redireciona
  //    pro /admin (que então vai exigir nova autenticação SSO + 2FA)
  window.location.href = CF_ACCESS_LOGOUT_URL;
}

const PRODUTO = {
  nome: "Sentinela",
  logo: { claro: "/logo-use.svg", escuro: "/logo-use-white.svg", alt: "Use Telecom" },
  href: "/admin",
  sufixoDoTitulo: "Sentinela",
} as const;

/** Link do React Router no contrato da casca (navegação sem recarregar). */
function renderLink({ href, className, ariaCurrent, ariaLabel, aoClicar, externo, children }: PropsLinkShell) {
  if (externo) {
    return <a href={href} className={className} aria-label={ariaLabel} target="_blank" rel="noopener noreferrer" onClick={aoClicar}>{children}</a>;
  }
  return <Link to={href} className={className} aria-current={ariaCurrent} aria-label={ariaLabel} onClick={aoClicar}>{children}</Link>;
}

export function Layout() {
  const { preferencia, definir } = useTema();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logado = lerUsuarioAdmin();
  const papel = logado?.role ?? null;

  const usuario: UsuarioShell | null = logado
    ? { nome: logado.nome, email: logado.email, papelRotulo: rotuloDoPapel(logado.role) }
    : null;

  const aoIrPara = useCallback((href: string) => navigate(href), [navigate]);

  // Pílulas do menu (SEN-30): uma rota leve, ao navegar e a cada 60 s com a aba visível. Falhou = sem pílula.
  const contagens = useContagensDoMenu(pathname, papel !== null);
  const itens = useMemo(() => aplicarContagens(ITENS, contagens), [contagens]);

  return (
    <CascaAdmin
      produto={PRODUTO}
      grupos={GRUPOS}
      itens={itens}
      papel={papel}
      rotuloDoPapel={rotuloDoPapel}
      usuario={usuario}
      pathname={pathname}
      preferenciaTema={preferencia}
      aoMudarTema={definir}
      aoSair={fazerLogout}
      renderLink={renderLink}
      aoIrPara={aoIrPara}
      // `/admin` é a porta de entrada: o Financeiro é levado a Circuitos por `PaginaInicial` (App.tsx),
      // e isso NÃO é rota barrada — não deve cair no "Sem acesso".
      rotasLivres={["/admin"]}
    >
      {/* Suspense aqui (e não no App) mantém barra lateral e barra superior visíveis enquanto o chunk da
          página carrega sob demanda. */}
      <Suspense fallback={<div className="py-10"><Carregando texto="Carregando página…" /></div>}>
        <Outlet />
      </Suspense>
      {/* Novidades não-vistas do ChangeLog — aparece uma vez pós-login */}
      <NovidadesModal />
    </CascaAdmin>
  );
}
